import { useMemo } from 'react';
import { useRouter } from 'next/router';

import { Box, Icon, Paragraph, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconDeleteOutline, IconPanorama360Outline } from '@zillow/constellation-icons';

import {
  useRentalManagerUserPropertyEditWebApiHandler_videoUrl,
  useRentalManagerUserPropertyFloorPlanUnitWebApiHandler_videoUrl,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';

import AddVideoLinkModalTrigger from '../../../modals/AddVideoLinkModalTrigger';

import { containerStyles } from './styles';

import { useAuth } from '../../../AuthProvider';

const VideoFormField = ({
  unitId,
  unitVideoUrl,
  isDisabled,
}: {
  unitId?: string;
  unitVideoUrl?: string;
  isDisabled?: boolean;
}) => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const videoUrl = unitId ? unitVideoUrl : listingDetails?.videoUrl || '';
  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const isArchived = listingDetails?.isArchived ?? false;
  const isUpdateListingRestricted = listingDetails?.restrictions?.isUpdateListingRestricted ?? false;
  const isSelfServeOnboardingInProgress =
    listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress ?? false;
  const isSSOBUpdateRestricted = listingDetails?.selfServeOnboardingStatus?.isUpdateRestricted ?? false;

  const feedModelId = useMemo(() => {
    const splitUnitId = unitId?.split('/');

    return splitUnitId?.[splitUnitId.length - 1] || '';
  }, [unitId]);

  const { mutateAsync: deleteListingVideoLink } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_videoUrl,
    successToastMessage: 'Video deleted',
    onError: () =>
      enqueueToast(
        <Toast tone="critical">Error occurred when deleting video link, refresh the page to try again.</Toast>,
      ),
  });

  const { mutateAsync: deleteUnitVideoLink } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanUnitWebApiHandler_videoUrl,
    successToastMessage: 'Video deleted',
    onError: () =>
      enqueueToast(
        <Toast tone="critical">Error occurred when deleting video link, refresh the page to try again.</Toast>,
      ),
  });

  const isDeleteDisabled = unitId
    ? isDisabled
    : !canUpdateListing ||
      isArchived ||
      isUpdateListingRestricted ||
      (isSelfServeOnboardingInProgress && isSSOBUpdateRestricted);

  const handleDeleteUrl = async () => {
    if (unitId) {
      await deleteUnitVideoLink({
        postData: { feedModelId, listingAlias, videoUrl: '' },
      });
    } else {
      await deleteListingVideoLink({
        getParams: { listingAlias, videoUrl: '' },
      });
    }
  };

  return videoUrl ? (
    <Box css={{ display: 'flex', alignItems: 'center', gap: '200', overflow: 'hidden' }}>
      {!isDeleteDisabled && (
        <Icon
          render={<IconDeleteOutline />}
          data-testid="add-video-delete-button"
          size="sm"
          onClick={handleDeleteUrl}
          css={{ color: 'icon.accent.blue.hero', cursor: 'pointer', minWidth: '400' }}
        />
      )}
      <TextButton asChild>
        <a target="_blank" href={videoUrl} rel="noopener noreferrer">
          {videoUrl}
        </a>
      </TextButton>
    </Box>
  ) : (
    <Box css={containerStyles}>
      <Box css={{ marginBottom: '400' }}>
        <Icon size="lg" render={<IconPanorama360Outline />} />
      </Box>
      <Paragraph css={{ marginBottom: '400' }}>Add a video link</Paragraph>
      <AddVideoLinkModalTrigger
        isDisabled={isDeleteDisabled}
        type={unitId ? 'unit' : 'listing'}
        feedModelId={feedModelId}
      />
    </Box>
  );
};

export default VideoFormField;
